import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from '../../i18n/useTranslation';
import { booksellerTestService, type BooksellerDto, type BooksellerAuthTestResult } from '../../services/booksellerTestService';
import Layout from '../../components/Layout';

const BooksellerAuthTestPage: React.FC = () => {
  const { t } = useTranslation();
  const [booksellers, setBooksellers] = useState<BooksellerDto[]>([]);
  const [selectedBooksellerId, setSelectedBooksellerId] = useState<string>('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<BooksellerAuthTestResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchParams] = useSearchParams();
  const preselectedBooksellerId = searchParams.get('bookseller');

  // Load booksellers on mount
  useEffect(() => {
    loadBooksellers();
  }, []);

  const loadBooksellers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await booksellerTestService.getAllBooksellers();
      setBooksellers(data);
      if (preselectedBooksellerId && data.some((b) => b.id === preselectedBooksellerId)) {
        setSelectedBooksellerId(preselectedBooksellerId);
      } else if (data.length > 0) {
        setSelectedBooksellerId(data[0].id);
      }
    } catch (err: any) {
      console.error('Failed to load booksellers:', err);
      setError(err.message || 'Failed to load booksellers');
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    if (!selectedBooksellerId) {
      setError(t('App.Admin.BooksellerTest.BooksellerAuthTestPage.SelectBooksellerError'));
      return;
    }

    try {
      setTesting(true);
      setError(null);
      setTestResult(null);
      const result = await booksellerTestService.testAuthentication(selectedBooksellerId);
      setTestResult(result);
    } catch (err: any) {
      console.error('Authentication test failed:', err);
      setError(err.message || 'Authentication test failed');
      setTestResult({
        success: false,
        errorMessage: err.message || 'Authentication test failed'
      });
    } finally {
      setTesting(false);
    }
  };

  const selectedBookseller = booksellers.find(b => b.id === selectedBooksellerId);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t('App.Admin.BooksellerTest.BooksellerAuthTestPage.Title')}
          </h1>
          <p className="text-gray-600">
            {t('App.Admin.BooksellerTest.BooksellerAuthTestPage.Subtitle')}
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6">
            <p className="text-primary-800">{t('App.Admin.BooksellerTest.BooksellerAuthTestPage.LoadingBooksellers')}</p>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-red-800 mb-1">{t('App.Admin.BooksellerTest.BooksellerAuthTestPage.ErrorLabel')}</h3>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Test Form */}
        {!loading && (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('App.Admin.BooksellerTest.BooksellerAuthTestPage.TestConfigTitle')}</h2>

            {/* Bookseller Selection */}
            <div className="mb-6">
              <label htmlFor="bookseller" className="block text-sm font-medium text-gray-700 mb-2">
                {t('App.Admin.BooksellerTest.BooksellerAuthTestPage.SelectBooksellerLabel')}
              </label>
              <select
                id="bookseller"
                value={selectedBooksellerId}
                onChange={(e) => setSelectedBooksellerId(e.target.value)}
                disabled={testing}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                {booksellers.length === 0 && (
                  <option value="">{t('App.Admin.BooksellerTest.BooksellerAuthTestPage.NoBooksellerOption')}</option>
                )}
                {booksellers.map((bookseller) => (
                  <option key={bookseller.id} value={bookseller.id}>
                    {bookseller.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Selected Bookseller Info */}
            {selectedBookseller && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 mb-2">{t('App.Admin.BooksellerTest.BooksellerAuthTestPage.BooksellerDetailsTitle')}</h3>
                <dl className="space-y-1">
                  <div className="flex">
                    <dt className="text-sm text-gray-600 w-24">{t('App.Admin.BooksellerTest.BooksellerAuthTestPage.DetailName')}</dt>
                    <dd className="text-sm font-medium text-gray-900">{selectedBookseller.name}</dd>
                  </div>
                  {selectedBookseller.description && (
                    <div className="flex">
                      <dt className="text-sm text-gray-600 w-24">{t('App.Admin.BooksellerTest.BooksellerAuthTestPage.DetailDescription')}</dt>
                      <dd className="text-sm text-gray-700">{selectedBookseller.description}</dd>
                    </div>
                  )}
                </dl>
              </div>
            )}

            {/* Test Button */}
            <button
              onClick={handleTest}
              disabled={testing || !selectedBooksellerId || booksellers.length === 0}
              className="w-full bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center"
            >
              {testing ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('App.Admin.BooksellerTest.BooksellerAuthTestPage.TestingButton')}
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {t('App.Admin.BooksellerTest.BooksellerAuthTestPage.TestButton')}
                </>
              )}
            </button>

            {/* What This Tests */}
            <div className="mt-6 p-4 bg-primary-50 border border-primary-200 rounded-lg">
              <h3 className="text-sm font-semibold text-primary-900 mb-2">{t('App.Admin.BooksellerTest.BooksellerAuthTestPage.WhatThisTestsTitle')}</h3>
              <ul className="text-sm text-primary-800 space-y-1 list-disc list-inside">
                <li>{t('App.Admin.BooksellerTest.BooksellerAuthTestPage.TestItem1')}</li>
                <li>{t('App.Admin.BooksellerTest.BooksellerAuthTestPage.TestItem2')}</li>
                <li>{t('App.Admin.BooksellerTest.BooksellerAuthTestPage.TestItem3')}</li>
                <li>{t('App.Admin.BooksellerTest.BooksellerAuthTestPage.TestItem4')}</li>
                <li>{t('App.Admin.BooksellerTest.BooksellerAuthTestPage.TestItem5')}</li>
              </ul>
            </div>
          </div>
        )}

        {/* Test Results */}
        {testResult && (
          <div className={`border rounded-lg shadow-sm p-6 ${testResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <div className="flex items-start mb-4">
              {testResult.success ? (
                <svg className="w-6 h-6 text-green-600 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-6 h-6 text-red-600 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
              <div>
                <h3 className={`text-lg font-semibold ${testResult.success ? 'text-green-900' : 'text-red-900'}`}>
                  {testResult.success ? t('App.Admin.BooksellerTest.BooksellerAuthTestPage.ResultPassed') : t('App.Admin.BooksellerTest.BooksellerAuthTestPage.ResultFailed')}
                </h3>
                {testResult.errorMessage && (
                  <p className={`mt-2 text-sm ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                    {testResult.errorMessage}
                  </p>
                )}
              </div>
            </div>

            {testResult.details && (
              <div className="mt-4 p-4 bg-white border border-gray-200 rounded">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">{t('App.Admin.BooksellerTest.BooksellerAuthTestPage.DetailsTitle')}</h4>
                <pre className="text-xs text-gray-700 whitespace-pre-wrap break-words font-mono">
                  {testResult.details}
                </pre>
              </div>
            )}

            {testResult.success && (
              <div className="mt-4 p-4 bg-white border border-green-200 rounded">
                <h4 className="text-sm font-semibold text-green-900 mb-2">{t('App.Admin.BooksellerTest.BooksellerAuthTestPage.NextStepsTitle')}</h4>
                <ul className="text-sm text-green-800 space-y-1 list-disc list-inside">
                  <li>{t('App.Admin.BooksellerTest.BooksellerAuthTestPage.NextStep1')}</li>
                  <li>{t('App.Admin.BooksellerTest.BooksellerAuthTestPage.NextStep2')}</li>
                  <li>{t('App.Admin.BooksellerTest.BooksellerAuthTestPage.NextStep3')}</li>
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Help Section */}
        <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('App.Admin.BooksellerTest.BooksellerAuthTestPage.TroubleshootingTitle')}</h3>
          <div className="space-y-3 text-sm text-gray-700">
            <div>
              <h4 className="font-medium text-gray-900">{t('App.Admin.BooksellerTest.BooksellerAuthTestPage.TipAuthorityTitle')}</h4>
              <p>{t('App.Admin.BooksellerTest.BooksellerAuthTestPage.TipAuthorityDesc')}</p>
            </div>
            <div>
              <h4 className="font-medium text-gray-900">{t('App.Admin.BooksellerTest.BooksellerAuthTestPage.TipCredentialsTitle')}</h4>
              <p>{t('App.Admin.BooksellerTest.BooksellerAuthTestPage.TipCredentialsDesc')}</p>
            </div>
            <div>
              <h4 className="font-medium text-gray-900">{t('App.Admin.BooksellerTest.BooksellerAuthTestPage.TipRedirectTitle')}</h4>
              <p>{t('App.Admin.BooksellerTest.BooksellerAuthTestPage.TipRedirectDesc')}</p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default BooksellerAuthTestPage;
